import React, { useContext } from 'react';
import { getPromotedRegions } from '~/api/services/regions';
import SEO from '~/components/seo';
import styled from 'styled-components';
import Layout from '~/components/layout';

import dynamic from 'next/dynamic';
import Hero from '~/components/rentals/Hero';
import BoatsList from '~/components/boats/boatsList';
import { getBoatsByCoordinates } from '~/api/services/boats';
import type { BoatRental } from '~/utils/rentals';
import { boatRentals } from '~/utils/rentals';
import { ContentWrapper } from '~/styles/common';
import Pagination from '~/components/rentals/Pagination';
import router from 'next/router';
import { getCultureToken } from '~/utils/langUtils';
import { AppContext } from '~/providers/AppProvider';

const RentingIsEasy = dynamic(async () => await import('~/components/homepage/rentingIsEasy'));
const FeaturedDestinations = dynamic(async () => await import('~/components/homepage/featuredDestinations'));

export default function BoatRentals({
  regions,
  boats,
  boatRental,
  totalBoatsCount,
}: {
  regions: any;
  boats: any[];
  boatRental: BoatRental;
  totalBoatsCount: number;
}) {
  const appCtx = useContext(AppContext);

  const onSubmit = (page: number) => {
    const searchQuery: any = {
      latitudeSw: boatRental.queryParams.latitudeSw,
      longitudeSw: boatRental.queryParams.longitudeSw,
      latitudeNe: boatRental.queryParams.latitudeNe,
      longitudeNe: boatRental.queryParams.longitudeNe,
      near: boatRental.queryParams.near,
      currency: appCtx.state.currency,
      pageNumber: page,
    };

    if (appCtx.state.defaultCurrency === appCtx.state.currency) {
      delete searchQuery.currency;
    }

    router.push({
      pathname: `/${getCultureToken(appCtx)}search`,
      query: searchQuery,
    });
  };
  return (
    <Layout>
      <SEO description={boatRental.metaDescription} title={`${boatRental.pageTitle}`} titleSuffix={false} />
      <Hero boatRental={boatRental} imgSrc={boatRental.imgSrc} />
      <Wrapper>
        <ContentWrapper>
          <h2>
            {String(totalBoatsCount)}+ boats found in {boatRental.queryParams.near}
          </h2>
          <BoatsListWrapper>
            <BoatsList
              placeName={''}
              hoveredResultId={'xxx'}
              setHoveredResultId={() => {}}
              isMapShownFullScreen={false}
              isMapShown={false}
              boats={boats}
              boatsTotalNumber={totalBoatsCount}
              showTopWrapper={false}
              showPagination={false}
            />
          </BoatsListWrapper>
        </ContentWrapper>
        <Pagination
          totalItems={totalBoatsCount}
          pageSize={18}
          currentPage={1}
          onPageChange={(page) => {
            onSubmit(page);
          }}
        />
        <RentingIsEasy />
        {regions && <FeaturedDestinations regions={regions} />}
      </Wrapper>
    </Layout>
  );
}

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  h2 {
    margin: 0 0 14px;
    @media (min-width: 780px) {
      margin: 40px 0;
    }
  }
`;

const BoatsListWrapper = styled.div`
  @media (min-width: 780px) {
    margin: 0 -50px;
  }
`;

export async function getServerSideProps({ params, query }) {
  let regions = null;
  let boats = [];
  let totalBoatsCount = 0;
  const boatRental = boatRentals.find((rental) => rental.id === params.id);

  if (!boatRental) {
    return {
      notFound: true,
    };
  }

  const queryParams = boatRental.queryParams;

  try {
    const response = await getBoatsByCoordinates({
      xLanguage: query.lang,
      pageNumber: 1,
      pageSize: 18,
      orderBy: 'SortPriority',
      latitudeNe: queryParams.latitudeNe,
      latitudeSw: queryParams.latitudeSw,
      longitudeNe: queryParams.longitudeNe,
      longitudeSw: queryParams.longitudeSw,
    });

    boats = response.data.Data;
    totalBoatsCount = response.data.TotalRecordsCount;
  } catch (error) {
    console.error('Error fetching boats:', error);
  }

  try {
    const response = await getPromotedRegions({
      xLanguage: query.lang,
    });
    regions = response.data;
  } catch (e) {
    console.error('Error fetching regions:', e);
  }

  return {
    props: {
      regions,
      boatRental,
      boats,
      totalBoatsCount,
    },
  };
}
